import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

export const employeeKeys = {
  all: (fleetId: number) => ["employees", "idFleet", fleetId] as const,
  allEmployeeWithoutVehicle: (fleetId: number) =>
    ["employeesWithoutVehicle", "idFleet", fleetId] as const,
  employeeByID: (fleetId: number, idEmployee: number) =>
    [...employeeKeys.all(fleetId), "idEmployee", idEmployee] as const,
  assignmentHistory: (fleetId: number, idEmployee: number) =>
    [...employeeKeys.employeeByID(fleetId, idEmployee), "assignmentHistory"] as const,
  adminUsers: () => ["adminUsers"] as const,
};

/** Une ligne d'historique d'affectation. */
export interface TAssignmentHistoryEntry {
  assignmentId: number;
  vehicleId: number;
  vin: string | null;
  immatriculation: string | null;
  brand: string | null;
  brandModel: string | null;
  start_date: string | null;
  end_date: string | null;
  /**
   * Calcule par le serveur, pas ici.
   *
   * La regle (end_date NULL ou dans le futur) est la meme que celle qui decide
   * du statut du vehicule cote API. La reecrire en TypeScript garantirait que
   * les deux divergent -- c'est exactement ce qui faisait afficher "Affecte"
   * sur une fiche vehicule dont le panneau collaborateur disait "aucun".
   */
  is_active: number | boolean;
}

/**
 * Historique COMPLET des affectations d'un collaborateur, terminees comprises.
 *
 * Distinct de la liste des vehicules du collaborateur, qui ne renvoie que les
 * affectations vivantes : la fiche affiche "le vehicule de cette personne", ce
 * panneau affiche "ceux qu'elle a eus". Une seule requete ne peut pas repondre
 * aux deux.
 */
export const useGetAssignmentHistory = (fleetId: number, idEmployee: number) => {
  const { data, ...res } = useQuery({
    queryKey: employeeKeys.assignmentHistory(fleetId, idEmployee),
    queryFn: async (): Promise<TAssignmentHistoryEntry[]> => {
      const response = await axios.get(
        `/fleets/${fleetId}/employees/${idEmployee}/vehicles/history`
      );
      return response?.data ?? [];
    },
    enabled: !!fleetId && !!idEmployee,
  });

  return { history: data ?? [], ...res };
};

export const useGetAllEmployeeWithVehicle = (fleetId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: employeeKeys.all(fleetId),
    queryFn: async (): Promise<TEmployee[]> => {
      const data = await axios.get(`/companies/${fleetId}/employees-vehicle`);
      return data?.data?.users;
    },
    enabled: !!fleetId,
  });

  return { employees: data ?? [], ...res };
};

export const useGetAllEmployeeWithoutVehicle = (fleetId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: employeeKeys.allEmployeeWithoutVehicle(fleetId),
    queryFn: async (): Promise<TEmployeeWithoutVehicle[]> => {
      const data = await axios.get(`/companies/${fleetId}/employees`);
      return data?.data?.users;
    },
    enabled: !!fleetId,
  });

  return { employees: data ?? [], ...res };
};

export const useGetOneEmployee = (fleetId: number, idEmployee: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: employeeKeys.employeeByID(fleetId, idEmployee),
    queryFn: async (): Promise<TEmployeeOne> => {
      const data = await axios.get(
        `/companies/${fleetId}/employees/${idEmployee}`
      );
      return data?.data?.[0];
    },
    enabled: !!fleetId && !!idEmployee,
  });

  return { employee: data, ...res };
};

interface RequestUpdateUserAdmin {
  firstname: string;
  lastname: string;
  email: string;
}

export const useUpdateUserEmployee = (
  fleetId: number,
  idEmployee: number,
  onClose?: () => void,
  initUserAdmin?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newUserAdmin: RequestUpdateUserAdmin) => {
      const data = await axios.put(
        `/fleets/${fleetId}/employees/${idEmployee}`,
        newUserAdmin
      );
      return data.data;
    },
    onSuccess: (_) => {
      onClose && onClose();
      initUserAdmin && initUserAdmin();
      // Update cache user admin
      const keys = employeeKeys.employeeByID(fleetId, idEmployee);
      queryClient.refetchQueries({ queryKey: keys });

      toast.success(t("common.save", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export const useUpdateEmployeeAvatar = (
  fleetId: number,
  idEmployee: number,
  onSuccess?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    // avatar = storage URL, or null to remove the photo
    mutationFn: async (avatar: string | null) => {
      const data = await axios.put(
        `/fleets/${fleetId}/employees/${idEmployee}/avatar`,
        { avatar }
      );
      return data.data;
    },
    onSuccess: (_) => {
      const keys = employeeKeys.employeeByID(fleetId, idEmployee);
      queryClient.refetchQueries({ queryKey: keys });
      onSuccess && onSuccess();
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export const useDeleteEmployee = (fleetId: number, idEmployee: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await axios.delete(
        `/companies/${fleetId}/employees/${idEmployee}`
      );
      return data.data;
    },
    onSuccess: (_) => {
      const keys = employeeKeys.all(fleetId);
      const prev = queryClient.getQueryData<TEmployee[]>(keys);
      if (prev) {
        const res = prev.filter((employee) => employee.userId !== idEmployee);
        queryClient.setQueryData<TEmployee[]>(keys, res);
      }
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export enum UserRole {
  SUPER_ADMIN = "SUPER_ADMIN",
  FLEET_MANAGER = "FLEET_MANAGER",
  EMPLOYEE = "EMPLOYEE",
}

interface RequestInviteEmployee {
  role: UserRole;
  email: string;
  firstname: string;
  lastname: string;
}

export const useInviteEmployeeCompanie = (
  idCompanie: number,
  onClose?: () => void
) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (user: RequestInviteEmployee) => {
      const data = await axios.post(
        `/companies/${idCompanie}/employees/invitation`,
        user
      );
      return data.data;
    },
    onSuccess: (_) => {
      onClose && onClose();
      toast.success(t("common.sendInvitation", "Invitation send"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

/**
 * Reactivate a collaborator.
 *
 * PUT /companies/{id}/employees/{userId}/reactivate has existed on the API
 * since the deactivate route was added (routes/company.php), but no client
 * hook ever called it, so a deactivated collaborator could not be brought back
 * from the detail page - the header button stayed on "Deactivate" forever.
 */
export const useReactivateEmployee = (
  companyId: number,
  userId: number,
  onClose?: () => void,
  initUserAdmin?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await axios.put(
        `/companies/${companyId}/employees/${userId}/reactivate`
      );
      return data.data;
    },
    onSuccess: (_) => {
      onClose && onClose();
      initUserAdmin && initUserAdmin();
      queryClient.invalidateQueries({
        queryKey: employeeKeys.employeeByID(companyId, userId),
      });
      queryClient.invalidateQueries({ queryKey: employeeKeys.all(companyId) });

      toast.success(
        t("collaborator.collaboratorEnabled", "Collaborator reactivated")
      );
    },
    onError: (err: any) => {
      toast.error(
        t(
          `errorCollab.${err?.response?.data?.message}`,
          String(err?.response?.data?.message)
        )
      );
    },
  });
};

export const useDisabledEmployee = (
  companyId: number,
  userId: number,
  onClose?: () => void,
  initUserAdmin?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await axios.put(
        `/companies/${companyId}/employees/${userId}/deactivate`
      );
      return data.data;
    },
    onSuccess: (_) => {
      onClose && onClose();
      initUserAdmin && initUserAdmin();
      // Was `refetchQueries(keys)` - the bare-array signature was removed in
      // react-query v5, so this silently matched nothing and the page kept
      // showing stale status until a hard reload.
      queryClient.invalidateQueries({
        queryKey: employeeKeys.employeeByID(companyId, userId),
      });
      queryClient.invalidateQueries({ queryKey: employeeKeys.all(companyId) });

      toast.success(
        t("collaborator.collaboratorDisabled", "Collaborator disabled")
      );
    },
    onError: (err: any) => {
      toast.error(
        t(
          `errorCollab.${err?.response?.data?.message}`,
          String(err?.response?.data?.message)
        )
      );
    },
  });
};
